import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import classes from './SelectReoccurrence.module.scss';
import { WMSelect, WMSelectRef } from '@walkme/wm-ui';
import { INotificationRecurrence } from '@communication-center/data-objects';
import { customReoccurrenceModal } from './CustomReoccurrenceModal';
import { enqueueSnackbar } from '../../../../../state/actions/ui';
import { useDispatch } from 'react-redux';
import { reoccurrencesDescription } from './reoccurrencesDescription';
import { EndReoccurrenceSelect } from './EndReocurrence';
import { useTranslation } from 'react-i18next';
import useSelector from '../../../../../infrastructure/useSelector';
import dayjs from 'dayjs';
import 'dayjs/locale/ja'
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

const keyPrefix = 'components.editNotification.editNotificationContent.sendStepDrawer.selectReoccurrence.selectReoccurrence';

export enum ReoccurrenceTypeSelect {
    ONCE,
    DAILY,
    WEEKLY_ON_DAY,
    MONTHLY_ON_WEEK_ON_DAY,
    MONTHLY_ON_MONTH_DAY,
    ANNUALLY,
    CUSTOM
}

export const ModalContext = React.createContext<{ value: string; label: string }[]>(null as $TSFixMe);

export const SelectReoccurrence = ({
    startDate,
    endDate,
    saveAsUTC = true,
    onChange,
    reoccurrenceCurrentData
}: {
    startDate: Date;
    endDate: Date;
    saveAsUTC?: boolean;
    onChange: (reoccurrenceData?: INotificationRecurrence) => void;
    reoccurrenceCurrentData?: INotificationRecurrence;
}) => {
    const { t } = useTranslation('', { keyPrefix });
    const locale = useSelector(state => state.ui.languageCode);
    const dispatch = useDispatch();
    const getInitialState = useCallback((value: any) => {
        let reoccurrenceTypeFromValue = ReoccurrenceTypeSelect.ONCE;

        if (!value) return reoccurrenceTypeFromValue;

        if (value.dayRules?.daysRepeat == 1 && !value.weekRules && !value.monthRules && !value.yearRules) {
            reoccurrenceTypeFromValue = ReoccurrenceTypeSelect.DAILY;
        } else if (value.weekRules?.weeksRepeat == 1 && value.dayRules?.daysInWeek?.length == 1 && !value.monthRules && !value.yearRules) {
            reoccurrenceTypeFromValue = ReoccurrenceTypeSelect.WEEKLY_ON_DAY;
        } else if (
            value.monthRules?.monthsRepeat == 1 &&
            value.dayRules?.daysInWeek?.length == 1 &&
            value.weekRules?.weeksInMonth?.length == 1 &&
            !value.yearRules
        ) {
            reoccurrenceTypeFromValue = ReoccurrenceTypeSelect.MONTHLY_ON_WEEK_ON_DAY;
        } else if (
            value.yearRules?.yearsRepeat == 1 &&
            value.dayRules?.daysInMonth?.length == 1 &&
            value.monthRules?.monthsInYear?.length == 1 &&
            !value.weekRules
        ) {
            reoccurrenceTypeFromValue = ReoccurrenceTypeSelect.ANNUALLY;
        } else if (value.dayRules || value.weekRules || value.monthRules || value.yearRules) {
            reoccurrenceTypeFromValue = ReoccurrenceTypeSelect.CUSTOM;
        }

        return reoccurrenceTypeFromValue;
    }, []);

    const [newRecurrenceData, setNewRecurrenceData] = useState(reoccurrenceCurrentData);

    const [recurrenceType, setRecurrenceType] = useState(getInitialState(reoccurrenceCurrentData));

    const options = useMemo(() => {
        const options: $TSFixMe = [];

        options.push({
            label: t('none'),
            value: ReoccurrenceTypeSelect.ONCE
        });
        if (dayjs(endDate).diff(dayjs(startDate), 'hours', true) >= 24)
            options.push({
          label: t('daily'),
          value: ReoccurrenceTypeSelect.DAILY
            });

        if (dayjs(endDate).diff(dayjs(startDate), 'weeks') >= 1)
            options.push({
          label: t('weekly', {
              startDate: dayjs(startDate).locale(locale).format('dddd')
          }),
          value: ReoccurrenceTypeSelect.WEEKLY_ON_DAY
            });

        if (dayjs(endDate).diff(dayjs(startDate), 'months') >= 1)
            options.push({
          label: t('monthly', {
              number: t(`numbersStr.${numbersStr[getNumberOfDayInMonth(startDate)]}`),
              startDate: dayjs(startDate).locale(locale).format('dddd')
          }),
          value: ReoccurrenceTypeSelect.MONTHLY_ON_WEEK_ON_DAY
            });

        if (dayjs(endDate).diff(dayjs(startDate), 'years') >= 1)
            options.push({
          label: t('annually', {
              month: dayjs(startDate).locale(locale).format('MMMM'),
              startDate: startDate.getDate()
          }),
          value: ReoccurrenceTypeSelect.ANNUALLY
            });

        if (dayjs(endDate).diff(dayjs(startDate), 'hours', true) >= 24)
            options.push({
          label: t('custom'),
          value: ReoccurrenceTypeSelect.CUSTOM
            });

        return options;
    }, [t, endDate, startDate, locale]);

    useEffect(() => {
        // @ts-expect-error ts-migrate(2339) FIXME: Property 'value' does not exist on type 'never'.
        if (!options.find(option => option.value === recurrenceType)) {
            setRecurrenceType(ReoccurrenceTypeSelect.ONCE);
        }
    }, [options, recurrenceType]);

    const customRecurrenceModalOpen = useCallback(
        async (startDate: any, endDate: any, currentReoccurrenceData: any) => {
            const customRecurrenceToaster = async (newReoccurrenceData: INotificationRecurrence, startDate: Date, endDate: Date) => {
                dispatch(
                  enqueueSnackbar({
                    message: t('snackbar.notificationStartOn', {
                      startDate: dayjs(startDate).locale(locale).format('LLL'),
                      newReoccurrenceData: reoccurrencesDescription(newReoccurrenceData),
                      endDate: dayjs(endDate).locale(locale).format('LL')
                    }),
                    action: {
                      callback: () => customRecurrenceModalOpen(startDate, endDate, newReoccurrenceData),
                      text: t('snackbar.edit')
                    },
                    options: { variant: 'success' }
                  })
                );
            };

            setCustomeModalOpen(true);

            await customReoccurrenceModal({
                startDate,
                endDate,
                currentReoccurrenceData,
                onChange: (newReoccurrenceData: INotificationRecurrence, startDate: Date, endDate: Date) => {
                    if (newReoccurrenceData) {
                        customRecurrenceToaster(newReoccurrenceData, startDate, endDate);
                        setRecurrenceType(getInitialState(newReoccurrenceData));
                        setNewRecurrenceData(newReoccurrenceData);
                    }
                }
            } as $TSFixMe).finally(() => setCustomeModalOpen(false));
        },
        [dispatch, getInitialState, locale, t]
    );

    const selectRef = useRef<WMSelectRef>(null);

    const onReoccurrenceSelect = useCallback(
        async (selected: $TSFixMe) => {
            if (selected.value === ReoccurrenceTypeSelect.CUSTOM) {
                customRecurrenceModalOpen(startDate, endDate, reoccurrenceCurrentData);
                selectRef.current?.blur();
            } else {
                setRecurrenceType(selected.value);
            }
        },
        [startDate, endDate, reoccurrenceCurrentData, customRecurrenceModalOpen]
    );

    useEffect(() => {
        if (recurrenceType !== ReoccurrenceTypeSelect.CUSTOM) {
            const calculatedRecurrenceScheduling = calcReoccurrenceData(recurrenceType, startDate, saveAsUTC);
            setNewRecurrenceData(state =>
                calculatedRecurrenceScheduling
                    ? {
                          ...calculatedRecurrenceScheduling,
                          endRecurrenceButtons: calculatedRecurrenceScheduling === null ? undefined : state?.endRecurrenceButtons
                      }
                    : undefined
            );
        }
    }, [recurrenceType, startDate, saveAsUTC]);

    const onEndRecurrenceButtonsSelect = useCallback((newEndButtons: any) => {
        setNewRecurrenceData(state => ({
            ...state,
            endRecurrenceButtons: newEndButtons
        }));
    }, []);

    useEffect(() => {
        if (newRecurrenceData !== reoccurrenceCurrentData) {
            onChange(newRecurrenceData);
        }
    }, [newRecurrenceData, onChange, reoccurrenceCurrentData]);

    const [isCustomModalOpen, setCustomeModalOpen] = useState(false);

    const selectOptionValue = useMemo(
        () =>
            isCustomModalOpen
                ? {
                      label: t('custom'),
                      value: ReoccurrenceTypeSelect.CUSTOM
                  }
                : options.find((option: $TSFixMe) => option.value === recurrenceType) || options[0],
        [isCustomModalOpen, options, recurrenceType, t]
    );

    return (
        <>
            <div className={classes.inputWrapper}>
                <div className={classes.inputTitle} data-testid={'recurrence-option-select-title'}>
                    <span>{t('recurrence')}</span>
                </div>
                <div data-testid={'recurrence-option-select'}>
                    <WMSelect
                        ref={selectRef}
                        value={selectOptionValue}
                        dropdownSelectProps={{
                            buttonProps: {
                                disabled: isDisabledSelect(options)
                            }
                        }}
                        isDropdownSelect={isDisabledSelect(options)}
                        isClearable={false}
                        onChange={onReoccurrenceSelect}
                        options={options}
                        className={classes.select}
                        menuPlacement="auto"
                    />
                </div>
            </div>
            {recurrenceType !== ReoccurrenceTypeSelect.ONCE && (
                <div className={classes.inputWrapper}>
                    <div className={classes.inputTitle} data-testid={'end-recurrence-option-select-title'}>
                        <span>{t('endRecurrenceAction')}</span>
                    </div>
                    <EndReoccurrenceSelect
                        reoccurrenceEndButtons={reoccurrenceCurrentData?.endRecurrenceButtons}
                        onChange={onEndRecurrenceButtonsSelect}
                    />
                </div>
            )}
        </>
    );
};

export const numbersStr = ['first', 'second', 'third', 'fourth', 'fifth'];
export const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
export const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function getNumberOfDayInMonth(date: Date) {
    return Math.floor(date.getDate() / 7);
}

function isDisabledSelect(options: $TSFixMe) {
    return options.length == 1;
}

function calcReoccurrenceData(recurrenceType: $TSFixMe, startDate: $TSFixMe, saveAsUTC: $TSFixMe) {
    let recurrenceData: INotificationRecurrence | null = {
        activePeriod: {
            type: 'days',
            amount: 1
        }
    };

    let date = dayjs(startDate);
    if (saveAsUTC) {
      date = dayjs(startDate).utc();
    }

    switch (recurrenceType) {
        case ReoccurrenceTypeSelect.ONCE:
            recurrenceData = null;
            break;
        case ReoccurrenceTypeSelect.DAILY:
            recurrenceData.dayRules = {
                daysRepeat: 1
            };
            break;
        case ReoccurrenceTypeSelect.WEEKLY_ON_DAY:
            recurrenceData.weekRules = {
                weeksRepeat: 1
            };
            recurrenceData.dayRules = {
                daysInWeek: [date.day()]
            };
            break;
        case ReoccurrenceTypeSelect.MONTHLY_ON_WEEK_ON_DAY:
            recurrenceData.monthRules = {
                monthsRepeat: 1
            };
            recurrenceData.weekRules = {
                weeksInMonth: [Math.floor(date.date() / 7)]
            };
            recurrenceData.dayRules = {
                daysInWeek: [date.day()]
            };
            break;

        case ReoccurrenceTypeSelect.ANNUALLY:
            recurrenceData.yearRules = {
                yearsRepeat: 1
            };
            recurrenceData.monthRules = {
                monthsInYear: [date.month()]
            };
            recurrenceData.dayRules = {
                daysInMonth: [date.date()]
            };
            break;
    }

    return recurrenceData;
}
